import React from 'react';
import { Card, Badge, Button } from 'react-bootstrap';
import moment from 'moment';

// Palette cycles through these for any event type
const TYPE_PALETTE = [
	{ color: '#1d9eb0', bg: 'rgba(29,158,176,0.1)', icon: 'fe-radio' },
	{ color: '#F68521', bg: 'rgba(246,133,33,0.1)', icon: 'fe-activity' },
	{ color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)', icon: 'fe-star' },
	{ color: '#10b981', bg: 'rgba(16,185,129,0.1)', icon: 'fe-zap' },
	{ color: '#ef4444', bg: 'rgba(239,68,68,0.1)',   icon: 'fe-heart' },
	{ color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', icon: 'fe-award' },
];

// Deterministic colour per type string so the same type always gets the same colour
const getTypeMeta = (eventType) => {
	if (!eventType) return TYPE_PALETTE[0];
	let hash = 0;
	for (let i = 0; i < eventType.length; i++) hash = eventType.charCodeAt(i) + ((hash << 5) - hash);
	return TYPE_PALETTE[Math.abs(hash) % TYPE_PALETTE.length];
};

const formatTypeLabel = (eventType) => {
	if (!eventType) return 'Event';
	return eventType
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (c) => c.toUpperCase());
};

const EventCard = ({ event, isRegistered, onRegister, registering }) => {
	const meta = getTypeMeta(event.eventType);
	const sessionDate = moment(event.sessionDatetime);
	const isUpcoming = sessionDate.isAfter(moment());
	const isFull =
		event.maxParticipants > 0 && event.registrationCount >= event.maxParticipants;

	const spotsLeft =
		event.maxParticipants > 0 ? event.maxParticipants - event.registrationCount : null;

	return (
		<Card className="event-card h-100">
			{/* Top accent bar */}
			<div className="event-card-accent" style={{ background: meta.color }} />

			<Card.Body className="p-4 d-flex flex-column">
				{/* Type + Status badges */}
				<div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
					<Badge
						bg=""
						className="event-type-badge"
						style={{ background: meta.bg, color: meta.color }}
					>
						<i className={`fe ${meta.icon} me-1`}></i>
						{formatTypeLabel(event.eventType)}
					</Badge>
					{isUpcoming ? (
						<Badge bg="" className="event-status-upcoming">Upcoming</Badge>
					) : (
						<Badge bg="" className="event-status-past">Past</Badge>
					)}
				</div>

				{/* Title */}
				<h5 className="event-title mb-2">{event.title}</h5>

				{/* Description */}
				{event.description && (
					<p className="event-description text-muted mb-3">{event.description}</p>
				)}

				{/* Speaker */}
				{event.speakerName && (
					<div className="event-speaker mb-3">
						<i className="fe fe-mic text-muted me-2"></i>
						<span className="speaker-name">{event.speakerName}</span>
						{event.speakerTitle && (
							<span className="speaker-title"> — {event.speakerTitle}</span>
						)}
					</div>
				)}

				{/* Date / Time / Duration */}
				<div className="event-meta-list mb-4">
					<div className="event-meta-item">
						<i className="fe fe-calendar me-2 text-muted"></i>
						<span>{sessionDate.format('ddd, MMM DD, YYYY')}</span>
					</div>
					<div className="event-meta-item">
						<i className="fe fe-clock me-2 text-muted"></i>
						<span>{sessionDate.format('h:mm A')}</span>
						{event.durationMinutes > 0 && (
							<span className="text-muted ms-1">
								({event.durationMinutes} min)
							</span>
						)}
					</div>
					{spotsLeft !== null && isUpcoming && (
						<div className="event-meta-item">
							<i className="fe fe-users me-2 text-muted"></i>
							<span
								className={spotsLeft <= 5 ? 'text-danger fw-semibold' : ''}
							>
								{isFull
									? 'Fully booked'
									: `${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left`}
							</span>
						</div>
					)}
				</div>

				{/* CTA — push to bottom */}
				<div className="mt-auto">
					{isRegistered ? (
						<div className="event-registered-badge">
							<i className="fe fe-check-circle me-2"></i>
							You're registered
						</div>
					) : isUpcoming && !isFull ? (
						<Button
							className="event-register-btn w-100"
							onClick={() => onRegister(event)}
							disabled={registering}
						>
							{registering ? (
								<>
									<span
										className="spinner-border spinner-border-sm me-2"
										role="status"
									></span>
									Registering...
								</>
							) : (
								<>
									<i className="fe fe-user-plus me-2"></i>
									Register for this Event
								</>
							)}
						</Button>
					) : isFull ? (
						<Button className="event-register-btn w-100" disabled>
							<i className="fe fe-slash me-2"></i>Fully Booked
						</Button>
					) : (
						<Button className="event-register-btn w-100" disabled>
							<i className="fe fe-calendar me-2"></i>Session Ended
						</Button>
					)}
				</div>
			</Card.Body>

			<style>{`
				.event-card {
					border: 1px solid #e2e8f0;
					border-radius: 16px;
					box-shadow: 0 2px 8px rgba(0,0,0,0.05);
					transition: transform 0.25s ease, box-shadow 0.25s ease;
					overflow: hidden;
					position: relative;
				}
				.event-card:hover {
					transform: translateY(-4px);
					box-shadow: 0 8px 24px rgba(0,0,0,0.1);
				}
				.event-card-accent {
					height: 4px;
					width: 100%;
				}
				.event-type-badge {
					font-size: 0.75rem;
					font-weight: 700;
					padding: 5px 12px;
					border-radius: 8px;
					display: inline-flex;
					align-items: center;
				}
				.event-status-upcoming {
					background: rgba(16,185,129,0.1) !important;
					color: #10b981 !important;
					font-size: 0.6875rem;
					font-weight: 700;
					padding: 4px 10px;
					border-radius: 6px;
				}
				.event-status-past {
					background: rgba(148,163,184,0.1) !important;
					color: #94a3b8 !important;
					font-size: 0.6875rem;
					font-weight: 700;
					padding: 4px 10px;
					border-radius: 6px;
				}
				.event-title {
					font-size: 1.0625rem;
					font-weight: 700;
					color: #1e293b;
					line-height: 1.4;
				}
				.event-description {
					font-size: 0.875rem;
					line-height: 1.6;
					display: -webkit-box;
					-webkit-line-clamp: 3;
					-webkit-box-orient: vertical;
					overflow: hidden;
				}
				.event-speaker {
					font-size: 0.875rem;
					color: #475569;
					display: flex;
					align-items: center;
				}
				.speaker-name {
					font-weight: 600;
				}
				.speaker-title {
					color: #94a3b8;
				}
				.event-meta-list {
					display: flex;
					flex-direction: column;
					gap: 0.5rem;
				}
				.event-meta-item {
					font-size: 0.875rem;
					color: #475569;
					display: flex;
					align-items: center;
				}
				.event-register-btn {
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					border: none;
					color: white;
					font-weight: 600;
					font-size: 0.9rem;
					padding: 0.625rem 1rem;
					border-radius: 10px;
					transition: opacity 0.2s ease;
					display: flex;
					align-items: center;
					justify-content: center;
				}
				.event-register-btn:hover:not(:disabled) {
					opacity: 0.9;
					color: white;
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
				}
				.event-register-btn:disabled {
					opacity: 0.6;
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					color: white;
				}
				.event-registered-badge {
					display: flex;
					align-items: center;
					justify-content: center;
					background: rgba(16,185,129,0.1);
					color: #10b981;
					font-weight: 700;
					font-size: 0.875rem;
					padding: 0.625rem 1rem;
					border-radius: 10px;
					border: 1.5px solid rgba(16,185,129,0.3);
				}
			`}</style>
		</Card>
	);
};

export default EventCard;
